import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OptimizedImage, getResponsiveImageSizes } from "../../utils/imageOptimizer";

const BLOGS_PER_PAGE = 9;

const BlogPage = () => {
  const { t } = useTranslation();
  const blog = t("blog", { returnObjects: true });
  const blogKeys = Object.keys(blog).filter(key => key.startsWith("blog"));
  // Sort by blog number descending for latest first
  const blogList = blogKeys
    .map(key => ({ ...blog[key], id: key.replace("blog", "") }))
    .sort((a, b) => Number(b.id) - Number(a.id));
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(blogList.length / BLOGS_PER_PAGE));

  const startIdx = (currentPage - 1) * BLOGS_PER_PAGE;
  const currentBlogs = blogList.slice(startIdx, startIdx + BLOGS_PER_PAGE);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center py-8 px-2">
      <h1 className="text-3xl md:text-6xl font-inter font-bold text-primary mb-16 text-center">{t("blog.listTitle")}</h1>
      <div className="w-full max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {currentBlogs.map((c) => (
          <div key={c.id} className="flex flex-col items-center">
            <div className="text-xs font-inter text-gray-400 mb-2 text-center">
              {c.date} | {c.author}
            </div>
            <Link to={`/blog/${c.id}`} className="w-full">
              <div className="w-full aspect-square bg-white border border-gray-400 rounded-2xl flex items-center justify-center overflow-hidden">
                <OptimizedImage 
                  src={c.image1} 
                  alt={c.title} 
                  className="object-cover w-full h-full" 
                  sizes={getResponsiveImageSizes('blog-thumbnail')}
                  loading="lazy"
                />
              </div>
            </Link>
            <Link to={`/blog/${c.id}`} className="mt-2 text-sm font-bold text-primary text-center hover:underline block">
              {c.title}
            </Link>
          </div>
        ))}
      </div>
      {/* Pagination (always visible) */}
      <div className="mt-12 flex justify-center items-center space-x-2 text-gray-400 text-sm">
        <span className={`cursor-pointer ${currentPage === 1 ? 'opacity-30 pointer-events-none' : ''}`} onClick={() => goToPage(1)}>&lt;&lt;</span>
        <span className={`cursor-pointer ${currentPage === 1 ? 'opacity-30 pointer-events-none' : ''}`} onClick={() => goToPage(currentPage - 1)}>&lt;</span>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => (
          <span
            key={num}
            className={`cursor-pointer px-1 ${currentPage === num ? 'text-primary font-bold underline' : ''}`}
            onClick={() => goToPage(num)}
          >
            {num}
          </span>
        ))}
        <span className={`cursor-pointer ${currentPage === totalPages ? 'opacity-30 pointer-events-none' : ''}`} onClick={() => goToPage(currentPage + 1)}>&gt;</span>
        <span className={`cursor-pointer ${currentPage === totalPages ? 'opacity-30 pointer-events-none' : ''}`} onClick={() => goToPage(totalPages)}>&gt;&gt;</span>
      </div>
    </div>
  );
};

export default BlogPage; 